/* eslint-disable no-use-before-define */
import default_image from '@/assets/imagePlaceholder.jpeg';
import { ApiProvider } from '@/provider/ApiProvider';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import InputBase from '@mui/material/InputBase';
import Popper from '@mui/material/Popper';
import { useEffect, useRef, useState,useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/system/Box';
import { Store as PropertyIcon } from '@mui/icons-material';
import {
  MultiSelectItem,
  MultiSelectPredefinedItem,
  SingleSelectItem,
} from '@/components/shared/SelectLists/Common';
import { useGlobalFilters } from '@/hooks/useGlobalFilters';
import BrandLogoWrapper from '@/components/styled/BrandLogoWrapper';

export default function PropertySelect() {
  const [anchorEl, setAnchorEl] = useState(null);
  const popperRef = useRef(null);
  const [value, setValue] = useState([]);
  const [PropertyLogos, setPropertyslogos] = useState([]);
  const [pendingValue] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedPropertyName, setSelectedPropertyName] = useState(null);
  const [singlePropertyFlag, setSinglePropertyFlag] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const limit = 20;
  const navigate = useNavigate();
  const { setHeaderProperty } = useGlobalFilters();
  const observerRef = useRef(null);

  // Set the selected property name.
  useEffect(() => {
    const property = PropertyLogos.find((item) => item.profile_img === selectedProperty);
    property && setSelectedPropertyName(property.full_name);
  }, [selectedProperty]);

  // Fetches a page of properties. When resetData is true, this replaces the
  // list (used for the initial load and whenever the search term changes).
  // Otherwise the results are appended, which is how additional pages are
  // loaded as the user scrolls, so accessible properties beyond the first
  // page (e.g. associated partners sorted later alphabetically) become
  // reachable without requiring a search.
  const fetchProperties = async (resetData = false, term = '') => {
    if (isLoading || (!hasMore && !resetData)) return;

    setIsLoading(true);

    try {
      const currentOffset = resetData ? 0 : offset;

      let body = new FormData();
      body.append('flag', 'active');
      body.append('offset', currentOffset);
      body.append('limit', limit);
      if (term) {
        body.append('term', term);
      }

      const response = await ApiProvider.webservicePostToken('Property/GetPropertyList', body, 'yes');

      if (response.success == true) {
        const newProperties = response.data.results || [];
        const totalCount = response.data.pagination?.total || 0;

        setHasMore(currentOffset + newProperties.length < totalCount);

        if (resetData && newProperties.length === 1) {
          localStorage.setItem('property_id', newProperties[0].id);
          localStorage.setItem('property_data', JSON.stringify(newProperties[0]));
        }

        if (resetData) {
          const withClear = [
            { id: 'clear', full_name: 'Remove Filter', profile_img: '', profile_logo_key: '' },
            ...newProperties,
          ];
          setPropertyslogos(withClear);
          setOffset(newProperties.length);
        } else {
          setPropertyslogos((prev) => {
            const existingIds = new Set(prev.map((item) => item.id));
            const uniqueNewProperties = newProperties.filter((item) => !existingIds.has(item.id));
            return [...prev, ...uniqueNewProperties];
          });
          setOffset((prev) => prev + newProperties.length);
        }
      }
    } catch (error) {
      ApiProvider.globalToastMessage(error, 5000, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load.
  const isFirstSearch = useRef(true);
  useEffect(() => {
    fetchProperties(true, '');
  }, []);

  // Re-fetch (from page 1) whenever the search term changes.
  useEffect(() => {
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      return;
    }

    setOffset(0);
    setHasMore(true);

    const timeoutId = setTimeout(() => {
      fetchProperties(true, searchTerm);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    const availableProperties = PropertyLogos.filter((item) => item.id !== 'clear');
    if (!availableProperties.length) {
      return;
    }

    setSinglePropertyFlag(availableProperties.length === 1);

    const selectedPropertyDataRaw = localStorage.getItem('property_data');
    if (selectedPropertyDataRaw) {
      const selectedPropertyData = JSON.parse(selectedPropertyDataRaw);
      setSelectedPropertyName(selectedPropertyData.full_name || null);
      setSelectedProperty(selectedPropertyData.profile_img || 'no image');
      return;
    }
  }, [PropertyLogos]);

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const PropertyInputChange = (event, value, reason) => {
    if (reason == 'input') {
      setSearchTerm(value);
    }
  };

  const handleClick = (event) => {
    //setPendingValue(value);
    setAnchorEl(event.currentTarget);
  };

  const closeDropdown = () => {
    setValue(pendingValue);
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const handleClose = (event, reason) => {
    if (reason === 'escape') {
      closeDropdown();
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'github-label' : undefined;

  const isInsideDropdown = (target) => {
    if (!(target instanceof Element)) {
      return false;
    }

    return Boolean(
      anchorEl?.contains(target) ||
        popperRef.current?.contains(target) ||
        target.closest('.brandLogoAutocompletePopper'),
    );
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDownOutside = (event) => {
      const target = event.target;
      if (isInsideDropdown(target)) {
        return;
      }
      closeDropdown();
    };

    document.addEventListener('pointerdown', handlePointerDownOutside, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDownOutside, true);
    };
  }, [open, anchorEl]);


  const lastOptionRef = useCallback(
      (el) => {
        observerRef.current?.disconnect();

        if (!el || !hasMore || isLoading) {
          return;
        }

        observerRef.current = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              fetchProperties(false, searchTerm);
            }
          },
          { threshold: 0.1 },
        );

        observerRef.current.observe(el);
      },
      [hasMore, isLoading, searchTerm],
    );

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => option.full_name,
  });

  return (
    <BrandLogoWrapper>
      <Box
        className={'brandLogoRootClass propertySelectorAnchor'}
        data-selector-anchor='property'
        sx={{ width: '100%' }}
      >
        {/* If we don't have predefined + can choose. */}
        {Boolean(selectedProperty == null) && singlePropertyFlag == false && (
          <MultiSelectItem
            icon={PropertyIcon}
            handleClick={handleClick}
            id={id}
            label='Select right holder/partner'
            entityLabel='Right Holder/Partner'
          />
        )}

        {/* If we have predefined + can choose. */}
        {Boolean(selectedProperty != null) && singlePropertyFlag == false && (
          <MultiSelectPredefinedItem
            handleClick={handleClick}
            imgSrc={selectedProperty !== 'no image' ? selectedProperty : default_image}
            imgAlt={'Property Logo'}
            selectedItemName={selectedPropertyName}
            entityLabel='Right Holder/Partner'
          />
        )}

        {singlePropertyFlag && (
          <SingleSelectItem
            imgSrc={selectedProperty !== 'no image' ? selectedProperty : default_image}
            imgAlt={'Property Logo'}
            selectedItemName={selectedPropertyName}
            entityLabel='Right Holder/Partner'
          />
        )}
      </Box>

      <Popper
        ref={popperRef}
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement='bottom-start'
        className={`brandLogoclassesPopper propertySelectPopper ${open ? 'selectorDropdownOpen' : ''}`}
        onPointerDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onTouchStart={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        sx={{ left: '65px !important', zIndex: 1700 }}
      >
        <div className={'brandLogoClassesHeader'}>Select Right Holder/Partner</div>
        <Autocomplete
          open
          onClose={handleClose}
          classes={{
            paper: 'brandLogoClassesPaper',
            option: 'brandLogoClassesOption',
            popperDisablePortal: 'brandLogoClassesPopperDisablePortal',
          }}
          style={{ backgroundColor: '#fff' }}
          slotProps={{
            popper: {
              className: 'brandLogoAutocompletePopper',
              sx: { zIndex: 1700 },
              onPointerDown: (event) => event.stopPropagation(),
              onMouseDown: (event) => event.stopPropagation(),
              onTouchStart: (event) => event.stopPropagation(),
              onClick: (event) => event.stopPropagation(),
            },
            listbox: {
              onPointerDown: (event) => event.stopPropagation(),
              onMouseDown: (event) => event.stopPropagation(),
              onTouchStart: (event) => event.stopPropagation(),
              onClick: (event) => event.stopPropagation(),
            },
            paper: {
              onPointerDown: (event) => event.stopPropagation(),
              onMouseDown: (event) => event.stopPropagation(),
              onTouchStart: (event) => event.stopPropagation(),
              onClick: (event) => event.stopPropagation(),
            },
          }}
          onInputChange={(event, value, reason) => PropertyInputChange(event, value, reason)}
          onChange={async (event, newValue) => {
            if (!newValue) {
              return;
            }
            const isClear = newValue.id == 'clear';
            const applied = await setHeaderProperty(isClear ? null : newValue);
            closeDropdown();
            if (!applied) {
              return;
            }
            if (!isClear) {
              setSelectedProperty(newValue.profile_img || 'no image');
              setSelectedPropertyName(newValue.full_name || null);
              const href = window.location.href;
              if (href.indexOf('propertyprofile') != -1) {
                const href1 = href.split('#/');
                const href2 = href1[1].split('/');
                navigate('/' + href2[0] + '/' + href2[1] + '/' + newValue.id);
              } else if (href.indexOf('partnerProfile') != -1) {
                if (localStorage.getItem('partnerProfileActiveTab')) {
                  localStorage.setItem(
                    'partnerPageTab',
                    localStorage.getItem('partnerProfileActiveTab'),
                  );
                }
                navigate('/partnerProfile/' + newValue.id);
              } else {
                navigate('/partnerProfile/' + newValue.id);
              }
            } else {
              setSelectedProperty(null);
              setSelectedPropertyName(null);
            }
          }}
          disableCloseOnSelect
          disablePortal
          renderTags={() => null}
          noOptionsText='No Right Holders/Partners'
          renderOption={(props, option, { index }) => {
            // Default image logic
            const imageSrc = option.profile_img || default_image;
            const isLast = index === PropertyLogos.length - 1;


            return (
              <li {...props} key={option.id} ref={isLast ? lastOptionRef : null}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <img
                    src={imageSrc}
                    onError={(e) => {
                      e.target.onerror = null; // Prevent infinite loop
                      e.target.src = default_image; // Fallback to default image
                    }}
                    style={{ width: '50px', height: '35px', objectFit: 'contain' }}
                    alt={option.full_name || 'Image'}
                  />
                  <div className='brandLogoClassesText' style={{ marginLeft: '10px' }}>
                    {option.full_name}
                  </div>
                </div>
              </li>
            );
          }}
          options={[...PropertyLogos].sort((a, b) => {
            // Display the selected labels first.
            let ai = value.indexOf(a);
            ai = ai == -1 ? value.length + PropertyLogos.indexOf(a) : ai;
            let bi = value.indexOf(b);
            bi = bi == -1 ? value.length + PropertyLogos.indexOf(b) : bi;
            return ai - bi;
          })}
          filterOptions={filterOptions}
          //getOptionLabel={(option) => option.full_name}
          renderInput={(params) => (
            <InputBase
              ref={params.InputProps.ref}
              inputProps={params.inputProps}
              autoFocus
              className={'BrandLogoClassesInputBase'}
            />
          )}
        />
      </Popper>
    </BrandLogoWrapper>
  );
}
